import React, { useState, useEffect, useRef } from 'react';
import API from '../api/axios';
import socket from '../socket';
import { Bell, Check, Trash2, Clock, ShieldCheck, Sparkles, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo } from '../utils/formatDate';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationCenter = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [soundSettings, setSoundSettings] = useState({ enabled: true, volume: 0.5, type: 'chime' });

    const dropdownRef = useRef(null);
    const audioRef = useRef(null);
    const lastSoundTime = useRef(0);
    const navigate = useNavigate();

    const fetchNotifications = async () => {
        if (!user) return;
        try {
            const res = await API.get('/notifications');
            setNotifications(res.data.notifications);
            setUnreadCount(res.data.unreadCount);
            console.log('[NOTIF_FETCH] Success:', res.data.unreadCount);
        } catch (err) {
            console.error('Notification Fetch Error:', err);
        }
    };

    const fetchSoundSettings = async () => {
        if (!user) return;
        const isAdmin = user.role === 'admin' || user.role === 'superadmin';
        if (!isAdmin) return;

        try {
            const res = await API.get('/settings');
            console.log('[NOTIF_SOUND_SETTINGS_FETCHED]', res.data);
            setSoundSettings({
                enabled: res.data.notification_sounds_enabled !== 'false',
                volume: res.data.notification_volume !== undefined ? parseFloat(res.data.notification_volume) : 0.5,
                type: res.data.notification_sound_type || 'chime'
            });
        } catch (err) {
            console.error('Error fetching sound settings:', err);
        }
    };

    const soundUrls = {
        chime: '/sounds/notification.mp3',
        minimal: '/sounds/minimal.mp3',
        success: '/sounds/success.mp3'
    };

    const fallbackUrls = {
        chime: 'https://assets.mixkit.co/active_storage/sfx/592/592-preview.mp3',
        minimal: 'https://assets.mixkit.co/active_storage/sfx/395/395-preview.mp3',
        success: 'https://assets.mixkit.co/active_storage/sfx/2357/2357-preview.mp3'
    };

    useEffect(() => {
        fetchNotifications();
        fetchSoundSettings();
    }, [user]);

    useEffect(() => {
        // Update audio instance when type changes
        const url = soundUrls[soundSettings.type] || soundUrls.chime;
        const fallback = soundUrls[soundSettings.type] || soundUrls.chime;

        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.crossOrigin = 'anonymous';

        // If local file fails, try fallback
        let tryFallback = true;
        audio.onerror = () => {
            if (tryFallback) {
                console.warn(`[NOTIF_SOUND] Local sound not found: ${url}. Falling back to CDN.`);
                tryFallback = false;
                audio.src = fallback;
                audio.load();
            } else {
                console.error(`[NOTIF_SOUND] Both local and fallback URLs failed. Sound will not play.`);
            }
        };

        audioRef.current = audio;
    }, [soundSettings.type]);

    const playNotificationSound = () => {
        const now = Date.now();
        const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

        console.log('[NOTIF_SOUND_ATTEMPT]', {
            enabled: soundSettings.enabled,
            isAdmin,
            visibility: document.visibilityState,
            hasAudio: !!audioRef.current,
            cooldown: now - lastSoundTime.current
        });

        // Prevent spam: minimum 2 second gap
        if (now - lastSoundTime.current < 2000) return;

        // Only play if enabled, admin is logged in, and tab is active
        if (
            soundSettings.enabled &&
            isAdmin &&
            document.visibilityState === 'visible' &&
            audioRef.current
        ) {
            console.log('[NOTIF_SOUND_PLAYING]', soundSettings.type);
            audioRef.current.volume = soundSettings.volume;
            audioRef.current.currentTime = 0;

            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => {
                    console.warn('[AUTOPLAY_RESTRICTION] Sound could not be played until user interaction.', err);
                });
            }

            lastSoundTime.current = now;

            // Trigger bell animation
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), 1500);
        } else {
            console.warn('[NOTIF_SOUND_SKIPPED] Conditions not met.');
        }
    };

    useEffect(() => {
        // Socket listener for real-time notifications
        const handleNewNotification = (data) => {
            console.log('[NOTIF_SOCKET_RECEIVED]', data);

            // Avoid adding if we just fetched (though unlikely in real-time)
            setNotifications(prev => [{
                id: Date.now(), // Temp ID for immediate display
                message: data.message,
                type: data.type || 'success',
                target_url: data.target_url,
                created_at: new Date().toISOString(),
                is_read: false
            }, ...prev]);
            setUnreadCount(prev => prev + 1);

            // Play sound for real-time arrivals
            playNotificationSound();
        };

        socket.on('payment_approved', handleNewNotification);
        socket.on('new_notification', handleNewNotification);
        socket.on('admin_notification', handleNewNotification);
        socket.on('new_property_submission', handleNewNotification);
        socket.on('vip_purchase', handleNewNotification);
        socket.on('support_message', handleNewNotification);
        socket.on('report', handleNewNotification);
        socket.on('new_payment', handleNewNotification);

        return () => {
            socket.off('payment_approved', handleNewNotification);
            socket.off('new_notification', handleNewNotification);
            socket.off('admin_notification', handleNewNotification);
            socket.off('new_property_submission', handleNewNotification);
            socket.off('vip_purchase', handleNewNotification);
            socket.off('support_message', handleNewNotification);
            socket.off('report', handleNewNotification);
            socket.off('new_payment', handleNewNotification);
        };
    }, [soundSettings, user]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        // Browser Autoplay Unlocker: Try to play/pause empty buffer on first click
        const unlockAudio = () => {
            if (audioRef.current) {
                audioRef.current.play().then(() => {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }).catch(() => { });
            }
            window.removeEventListener('click', unlockAudio);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('click', unlockAudio);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('click', unlockAudio);
        };
    }, []);

    const markAsRead = async (id) => {
        try {
            await API.put(`/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error(err);
        }
    };

    const markAllRead = async () => {
        try {
            await API.put('/notifications/mark-all-read');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error(err);
        }
    };

    const deleteNotification = async (e, id) => {
        e.stopPropagation();

        // If it's a temp ID (real-time notification not yet in DB), just remove locally
        if (typeof id === 'number' && id > 1000000000000) {
            setNotifications(prev => prev.filter(n => n.id !== id));
            setUnreadCount(prev => Math.max(0, prev - 1));
            return;
        }

        try {
            await API.delete(`/notifications/${id}`);
            setNotifications(prev => prev.filter(n => n.id !== id));
            // Only decrement unread if it was unread
            const notif = notifications.find(n => n.id === id);
            if (notif && !notif.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error(err);
            // Fallback: remove locally anyway to improve UX if API fails or record was already gone
            setNotifications(prev => prev.filter(n => n.id !== id));
        }
    };

    const deleteAllNotifications = async () => {
        try {
            await API.delete('/notifications');
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error(err);
        }
    };

    const handleNotificationClick = async (notif) => {
        if (!notif.is_read) {
            markAsRead(notif.id);
        }
        if (notif.target_url) {
            setIsOpen(false);
            navigate(notif.target_url);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'success': return <ShieldCheck size={14} className="text-green-500" />;
            case 'warning': return <AlertCircle size={14} className="text-amber-500" />;
            case 'premium': return <Sparkles size={14} className="text-secondary" />;
            default: return <Clock size={14} className="text-slate-400" />;
        }
    };

    useEffect(() => {
        const handleConnect = () => {
            console.log('[SOCKET_RECONNECTED] Re-fetching notifications...');
            fetchNotifications();
        };

        socket.on('connect', handleConnect);
        return () => {
            socket.off('connect', handleConnect);
        };
    }, [user]);

    const handleToggle = () => {
        const nextState = !isOpen;
        setIsOpen(nextState);
        if (nextState && unreadCount > 0) {
            markAllRead();
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <motion.button
                onClick={handleToggle}
                aria-label={`Notifications (${unreadCount} unread)`}
                animate={isAnimating ? {
                    rotate: [0, -10, 10, -10, 10, 0],
                    scale: [1, 1.1, 1],
                    transition: { duration: 0.5, repeat: 2 }
                } : {}}
                className={`relative w-12 h-12 rounded-2xl shadow-sm border flex items-center justify-center transition-all duration-300 ${unreadCount > 0 ? 'bg-red-400/10 border-secondary/20 text-secondary' : 'bg-white border-slate-100 text-slate-400 hover:text-primary'}`}
            >
                <Bell size={20} className={isAnimating ? "text-secondary" : ""} />

                <AnimatePresence>
                    {unreadCount > 0 && (
                        <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-lg border-2 border-red-300"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </motion.span>
                    )}
                </AnimatePresence>

                {/* Subtle pulse ring when new notification arrives */}
                {isAnimating && (
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0.5 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="absolute inset-0 bg-secondary/20 rounded-2xl -z-10"
                    />
                )}
            </motion.button>

            {isOpen && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 md:left-auto md:right-0 md:translate-x-0 mt-4 w-[calc(100vw-2rem)] sm:w-[400px] bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden z-[500] max-h-[85vh] md:max-h-[600px] flex flex-col"
                >
                    <div className="p-5 md:p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 gap-4">
                        <div className="min-w-0">
                            <h3 className="text-[11px] md:text-sm font-black text-primary uppercase tracking-widest truncate">Notification Ledger</h3>
                            <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 truncate">Real-time Transmission Hub</p>
                        </div>
                        {notifications.length > 0 && (
                            <button
                                onClick={deleteAllNotifications}
                                className="flex items-center gap-2 text-[10px] font-black text-slate-400 hover:text-red-500 uppercase tracking-widest shrink-0"
                            >
                                <Trash2 size={12} />
                                Purge All
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
                        {notifications.length > 0 ? (
                            <div className="divide-y divide-slate-50">
                                {notifications.map((notif) => (
                                    <div
                                        key={notif.id}
                                        onClick={() => handleNotificationClick(notif)}
                                        className={`p-6 relative group cursor-pointer ${!notif.is_read ? 'bg-secondary/[0.03] border-l-4 border-secondary' : 'border-l-4 border-transparent'}`}
                                    >
                                        <div className="flex gap-4">
                                            <div className="relative shrink-0">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${!notif.is_read ? 'bg-white shadow-sm' : 'bg-slate-50 text-slate-300'}`}>
                                                    {getTypeIcon(notif.type)}
                                                </div>
                                                {!notif.is_read && (
                                                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-secondary rounded-full border-2 border-white shadow-sm"></span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-xs leading-relaxed ${!notif.is_read ? 'font-black text-primary' : 'font-medium text-slate-500'}`}>
                                                    {notif.message}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">
                                                        {formatTimeAgo(notif.created_at)} • Institutional Signal
                                                    </span>
                                                    {!notif.is_read && (
                                                        <span className="text-[8px] font-black text-secondary uppercase tracking-tighter bg-secondary/10 px-1.5 py-0.5 rounded-full">New Transmission</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
                                                {!notif.is_read && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAsRead(notif.id);
                                                        }}
                                                        aria-label="Acknowledge notification"
                                                        className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-green-500 shadow-sm hover:bg-green-50 border border-slate-100"
                                                        title="Acknowledge Signal"
                                                    >
                                                        <Check size={14} strokeWidth={3} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => deleteNotification(e, notif.id)}
                                                    aria-label="Delete notification"
                                                    className={`w-8 h-8 bg-white rounded-lg flex items-center justify-center text-red-400 shadow-sm hover:bg-red-50 border border-slate-100 ${notif.target_url?.includes('admin-messages') ? 'hidden' : ''}`}
                                                    title="Delete Signal"
                                                >
                                                    <Trash2 size={14} strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-20 text-center">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-200">
                                    <Bell size={28} />
                                </div>
                                <h4 className="text-sm font-black text-slate-300 uppercase tracking-widest">No Active Transmissions</h4>
                            </div>
                        )}
                    </div>

                    <div className="p-6 bg-slate-50/50 border-t border-slate-50 text-center">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Institutional Oversight Protocol Active</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
